import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { addTag, normalizeTag, removeTag } from '@/lib/media';

interface MediaTagEditorProps {
  tags: string[];
  onChange: (tags: string[]) => void;
  /** Existing library tags, offered as quick-add suggestions so tag vocabulary stays consistent. */
  suggestions?: { tag: string; count: number }[];
}

/**
 * Adds/removes tags on a media item.
 *
 * Tags replace a folder hierarchy: an item can carry any number of them,
 * which suits media far better than folders — the same diagram might
 * belong to "bitcoin", "diagram", and "draft" at once, and forcing a
 * single location would mean duplicating the file or picking arbitrarily.
 *
 * Suggestions are surfaced from tags already in use because free-text
 * tagging drifts fast otherwise ("bitcoin" / "Bitcoin" / "btc"); making
 * the existing vocabulary one click away is what keeps it usable as a
 * filter.
 */
export function MediaTagEditor({ tags, onChange, suggestions = [] }: MediaTagEditorProps) {
  const [draft, setDraft] = useState('');

  const commit = () => {
    const normalized = normalizeTag(draft);
    if (!normalized) return;
    onChange(addTag(tags, normalized));
    setDraft('');
  };

  const unused = suggestions.filter((s) => !tags.includes(s.tag)).slice(0, 8);

  return (
    <div className="space-y-2">
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {tags.map((tag) => (
            <Badge key={tag} variant="secondary" className="gap-1 pr-1">
              {tag}
              <button
                type="button"
                onClick={() => onChange(removeTag(tags, tag))}
                aria-label={`Remove tag "${tag}"`}
                className="rounded-full p-0.5 hover:bg-background/60"
              >
                <X className="size-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            }
          }}
          placeholder="Add a tag…"
          className="h-9"
        />
        <Button type="button" variant="outline" size="sm" className="h-9 shrink-0" onClick={commit} disabled={!normalizeTag(draft)}>
          <Plus className="size-4" />
          Add
        </Button>
      </div>

      {unused.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {unused.map(({ tag, count }) => (
            <button
              key={tag}
              type="button"
              onClick={() => onChange(addTag(tags, tag))}
              className="rounded-full border border-dashed border-border px-2 py-0.5 text-xs text-muted-foreground transition-colors hover:border-solid hover:text-foreground"
            >
              {tag}
              <span className="ml-1 text-muted-foreground/60">{count}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
